import React, { useState, useRef } from 'react';

// ponytail: fixed identity-color palette (data, not theme); intentionally not tokenized
const AVATAR_PALETTE = [
  '#1982c4',
  '#e63946',
  '#2a9d8f',
  '#f9a825',
  '#7c3aed',
  '#059669',
  '#d97706',
  '#db2777',
  '#6366f1',
  '#0891b2',
];

const getHashColor = (seed) => {
  if (!seed) return AVATAR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const getInitials = ({ firstName, lastName, name }) => {
  if (firstName || lastName) {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    const combined = `${first}${last}`.trim();
    return combined || '?';
  }

  const source = (name || '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/);
  const first = parts[0] ? parts[0].charAt(0).toUpperCase() : '';
  const second = parts[1] ? parts[1].charAt(0).toUpperCase() : '';
  const combined = `${first}${second}`.trim();
  return combined || first || '?';
};

function CrmAvatar({ firstName, lastName, name, title, size = 40, avatarUrl, className, editable, onFileSelect }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const fileInputRef = useRef(null);
  const displayTitle = title || name || `${firstName || ''} ${lastName || ''}`.trim();
  const classes = ['team-avatar', className].filter(Boolean).join(' ');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && onFileSelect) {
      onFileSelect(file);
    }
    e.target.value = '';
  };

  const editOverlay = editable ? (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <div
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: hovered ? 1 : 0,
          transition: 'opacity 0.15s ease-in-out',
          cursor: 'pointer',
        }}
      >
        <i className="fal fa-camera" style={{ color: 'var(--sl-text-white)', fontSize: size * 0.35 }} />
      </div>
    </>
  ) : null;

  if (avatarUrl && !imgFailed) {
    return (
      <div
        style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
        onMouseEnter={editable ? () => setHovered(true) : undefined}
        onMouseLeave={editable ? () => setHovered(false) : undefined}
      >
        <img
          src={avatarUrl}
          alt={displayTitle}
          className={classes}
          onError={() => setImgFailed(true)}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
        {editOverlay}
      </div>
    );
  }

  const seed = firstName || lastName ? `${firstName || ''}${lastName || ''}` : (name || '');
  const backgroundColor = getHashColor(seed);
  const initials = getInitials({ firstName, lastName, name });

  return (
    <div
      style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
      onMouseEnter={editable ? () => setHovered(true) : undefined}
      onMouseLeave={editable ? () => setHovered(false) : undefined}
    >
      <div
        className={classes}
        style={{
          backgroundColor,
          width: size,
          height: size,
        }}
        title={displayTitle}
      >
        {initials}
      </div>
      {editOverlay}
    </div>
  );
}

export default CrmAvatar;
